import { IPendingHotlineQuestionnaire } from "../types/user.types";
import type { TCDRData, TCallerInfo, TIncomingCall } from "../../types/callCenter.types";
import type { MvoiceUserResponseDto } from "./mvoice.types";

function asObject(v: unknown): Record<string, unknown> | null {
  if (v !== null && typeof v === "object" && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

function pickStr(o: Record<string, unknown>, key: string): string | undefined {
  const v = o[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function pickNum(o: Record<string, unknown>, key: string): number | undefined {
  const v = o[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.length > 0) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function parseCallerUser(raw: unknown): MvoiceUserResponseDto | null {
  const o = asObject(raw);
  if (!o) return null;
  const id = pickNum(o, "id");
  if (id == null) return null;

  return {
    id,
    guid: typeof o.guid === "string" ? o.guid : o.guid == null ? null : String(o.guid),
    companyId: pickNum(o, "companyId") ?? 0,
    firstName: typeof o.firstName === "string" ? o.firstName : null,
    lastName: typeof o.lastName === "string" ? o.lastName : null,
    fullName: typeof o.fullName === "string" ? o.fullName : null,
    roleId: pickNum(o, "roleId") ?? 0,
    statusId: pickNum(o, "statusId") ?? 0,
    genderId: pickNum(o, "genderId") ?? null,
    migzarId: pickNum(o, "migzarId") ?? null,
    vetek: pickNum(o, "vetek") ?? null,
    phone: typeof o.phone === "string" ? o.phone : null,
    email: typeof o.email === "string" ? o.email : null,
    avatar: typeof o.avatar === "string" ? o.avatar : null,
    address: typeof o.address === "string" ? o.address : null,
    birth: typeof o.birth === "string" ? o.birth : null,
    fakeCompanyId: pickNum(o, "fakeCompanyId") ?? null,
    questionnaireCompaniesHeadersId:
      pickNum(o, "questionnaireCompaniesHeadersId") ?? null,
    usersMultipleCompanies: o.usersMultipleCompanies ?? null,
    about: typeof o.about === "string" ? o.about : null,
    video: typeof o.video === "string" ? o.video : null,
    allowPatientBooking:
      typeof o.allowPatientBooking === "boolean" ? o.allowPatientBooking : null,
    treatmentGroupId: pickNum(o, "treatmentGroupId") ?? null,
    treatmentGroupName:
      typeof o.treatmentGroupName === "string" ? o.treatmentGroupName : null,
  };
}

function firstLastFromUser(
  u: MvoiceUserResponseDto
): { firstName: string; lastName: string } {
  let first = u.firstName?.trim() ?? "";
  let last = u.lastName?.trim() ?? "";
  if (!first && !last && u.fullName?.trim()) {
    const parts = u.fullName.trim().split(/\s+/).filter(Boolean);
    first = parts[0] ?? "";
    last = parts.slice(1).join(" ") ?? "";
  }
  if (!first && !last) {
    first = `User ${u.id}`;
    last = "";
  }
  return { firstName: first, lastName: last };
}

function mapUserToCallerInfo(
  user: MvoiceUserResponseDto,
  cli: string,
  cycle?: number
): TCallerInfo {
  const { firstName, lastName } = firstLastFromUser(user);
  const qHeaderId = user.questionnaireCompaniesHeadersId;

  return {
    userId: user.id,
    guid: user.guid ?? "",
    firstName,
    lastName,
    phone: user.phone?.trim() || cli,
    currentCycle: cycle ?? 0,
    previousCycles: 0,
    ...(cycle != null ? { cycle } : {}),
    ...(qHeaderId != null && qHeaderId > 0
      ? { referralNumber: `#${qHeaderId}` }
      : {}),
  };
}

/** MVoiceIncomingCall — includes optional callerUser (UserResponseDto). */
export function mapMvoiceIncomingPayload(payload: unknown): TIncomingCall | null {
  const o = asObject(payload);
  if (!o) return null;

  const eventType = pickStr(o, "eventType");
  if (eventType && eventType !== "incoming" && eventType !== "start") {
    return null;
  }

  const callId = pickStr(o, "callId");
  if (!callId) return null;

  const callerUser = parseCallerUser(o.callerUser);
  const isIdentified = callerUser != null;

  const mvoiceCycle = pickNum(o, "cycle");
  const questionnaireCompaniesScoreUsersId = pickNum(
    o,
    "questionnaireCompaniesScoreUsersId"
  );
  const questionnaireCompaniesId = pickNum(o, "questionnaireCompaniesId");
  const questionnaireId = pickNum(o, "questionnaireId");
  const pendingQuestionnaire = parsePendingQuestionnaire(o.pendingQuestionnaire);

  /** Caller line shown in UI: root `phone` only, else `callId` — never use `callerPhone`. */
  const cli = pickStr(o, "phone") ?? callId;

  const timestamp =
    pickStr(o, "receivedAt") ??
    pickStr(o, "calledAt") ??
    pickStr(o, "startedAt") ??
    new Date().toISOString();

  const cNumberName = pickStr(o, "cNumberName");
  const dNumber = pickStr(o, "dNumber");

  const base: TIncomingCall = {
    callId,
    cli,
    isIdentified,
    timestamp,
    ...(cNumberName != null ? { cNumberName } : {}),
    ...(dNumber != null ? { dNumber } : {}),
    ...(isIdentified && callerUser
      ? {
          callerInfo: {
            ...mapUserToCallerInfo(callerUser, cli, mvoiceCycle),
            ...(questionnaireCompaniesScoreUsersId != null
              ? { questionnaireCompaniesScoreUsersId }
              : {}),
            ...(questionnaireCompaniesId != null ? { questionnaireCompaniesId } : {}),
            ...(questionnaireId != null ? { questionnaireId } : {}),
            ...(pendingQuestionnaire != null ? { pendingQuestionnaire } : {}),
          },
        }
      : {}),
  };

  return base;
}

/**
 * The hotline questionnaire block off the call-start payload, or null.
 *
 * Validated field by field rather than cast: this arrives over SignalR from a broadcast that also
 * carries telephony data, and a half-formed block would render a banner with an empty name on it.
 * Anything missing its name is treated as absent.
 */
function parsePendingQuestionnaire(value: unknown): IPendingHotlineQuestionnaire | null {
  const o = asObject(value);
  if (!o) return null;

  const name = pickStr(o, "name");
  if (!name) return null;

  return {
    name,
    reason: pickStr(o, "reason") ?? "ReturningCaller",
    stage: pickNum(o, "stage") ?? 0,
    isLastContact: o.isLastContact === true,
    // Null rather than absent when the server could not build one: the banner shows without a
    // button in that case, which is better than no banner.
    link: pickStr(o, "link") ?? null,
  };
}

function parseDurationSeconds(o: Record<string, unknown>): number | null {
  let durationSeconds = pickNum(o, "durationSeconds");
  if (durationSeconds != null && durationSeconds >= 0) {
    return Math.floor(durationSeconds);
  }
  const durStr = pickStr(o, "duration");
  if (durStr) {
    const n = parseFloat(durStr);
    if (Number.isFinite(n) && n >= 0) return Math.floor(n);
  }
  const durNum = pickNum(o, "duration");
  if (durNum != null && durNum >= 0) return Math.floor(durNum);
  return null;
}

/** MVoiceCallEnded — durationSeconds and/or duration (string|number). */
export function mapMvoiceEndedPayload(payload: unknown): TCDRData | null {
  const o = asObject(payload);
  if (!o) return null;

  const callId = pickStr(o, "callId");
  if (!callId) return null;

  const durationSeconds = parseDurationSeconds(o) ?? 0;

  const directionRaw = pickStr(o, "direction");
  const direction = directionRaw ?? "";

  const recordingUrl = pickStr(o, "recordingUrl");
  const wasRecorded = Boolean(recordingUrl && recordingUrl.length > 0);

  const startTime =
    pickStr(o, "startedAt") ?? pickStr(o, "calledAt") ?? new Date().toISOString();
  const endTime =
    pickStr(o, "receivedAt") ?? new Date().toISOString();

  return {
    callId,
    direction,
    duration: durationSeconds,
    startTime,
    endTime,
    ...(recordingUrl ? { recordingUrl } : {}),
    wasRecorded,
  };
}

export type TCallRecordNumberPayload = {
  callId: string;
  recordNumber: number;
};

/** Separate SignalR event — recordNumber arrives after recording is processed. */
export function mapMvoiceRecordNumberPayload(
  payload: unknown
): TCallRecordNumberPayload | null {
  const o = asObject(payload);
  if (!o) return null;

  const callId = pickStr(o, "callId") ?? pickStr(o, "CallId");
  if (!callId) return null;

  const recordNumber =
    pickNum(o, "recordNumber") ??
    pickNum(o, "RecordNumber") ??
    pickNum(o, "meetingRecordNumber");
  if (recordNumber == null || recordNumber <= 0) return null;

  return { callId, recordNumber: Math.floor(recordNumber) };
}
